import type * as React from 'react';
import type { FC, ReactNode } from 'react';
import {
  Box,
  Paper,
  Step,
  StepButton,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

/** Per-step status used during deep-link session restoration. */
export type StepInitStatus = 'idle' | 'loading' | 'success' | 'failed';

export interface StepType {
  title: string;
  component: React.ComponentType;
  /** Initialization status — only set during deep-link session restoration. */
  initStatus?: StepInitStatus;
  /** When true and `onStepClick` is set, the step title jumps to this step. */
  clickable?: boolean;
}

export interface StepperPaperProps {
  title?: string;
  steps: StepType[];
  activeStep: number;
  children?: ReactNode;
  topTabs?: ReactNode;
  onStepClick?: (index: number) => void;
}

// Stable component reference — must not be defined inside the render function
// or MUI will remount the icon on every render.
const LoadingStepIcon: FC = () => (
  <Box
    sx={{
      width: 24,
      height: 24,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <CircularProgress size={20} thickness={5} />
  </Box>
);

const StepperPaper: FC<StepperPaperProps> = ({
  title,
  steps,
  activeStep,
  children,
  topTabs,
  onStepClick,
}) => {
  return (
    <>
      {topTabs}
      <Box sx={{ flex: 1, display: 'flex' }}>
        <Paper
          variant="outlined"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            pt: { xs: 2, md: 3 },
            px: { xs: 2, md: 3 },
            border: 'none',
            borderRadius: '8px',
            overflow: 'hidden',
            overflowY: 'auto',
            boxShadow: '12px 13px 26px 5px rgb(163 145 203 / 6%)',
            width: '100%',
          }}
        >
          {title && (
            <Box sx={{ display: 'flex' }}>
              <Typography component="h1" variant="h6" align="left">
                {title}
              </Typography>
            </Box>
          )}
          <Stepper
            nonLinear={Boolean(onStepClick)}
            activeStep={activeStep}
            sx={{
              pt: 3,
              pb: 5,
              '& .MuiStepIcon-text': { fontSize: '0.975rem' },
              '& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed':
                {
                  color: 'primary.main',
                },
              '& .MuiStepLabel-label.Mui-active, & .MuiStepLabel-label.Mui-completed':
                {
                  color: 'primary.main',
                },
            }}
          >
            {steps.map((step, index) => {
              const { initStatus } = step;
              const hasInitStatus = initStatus !== undefined;
              const isClickable =
                Boolean(onStepClick) && Boolean(step.clickable);

              // nonLinear stops MUI from auto-completing earlier steps, so valid
              // clickable ones would render grey. Keep the numbered circle + title
              // in primary for the current step and any step that is valid to open.
              // `activeStep < 0` means no step is current (create-ads result view).
              const stepOverrides = hasInitStatus
                ? {
                    active: initStatus === 'loading',
                    completed: initStatus === 'success',
                  }
                : {
                    active:
                      activeStep >= 0 &&
                      (index === activeStep ||
                        (isClickable && index < activeStep)),
                    completed:
                      activeStep >= 0 && index < activeStep && !isClickable,
                  };

              return (
                <Step key={step.title} {...stepOverrides}>
                  {isClickable ? (
                    <StepButton
                      onClick={() => {
                        onStepClick?.(index);
                      }}
                      sx={{
                        '& .MuiStepLabel-label': {
                          cursor: 'pointer',
                        },
                      }}
                    >
                      {step.title}
                    </StepButton>
                  ) : (
                    <StepLabel
                      error={initStatus === 'failed'}
                      StepIconComponent={
                        initStatus === 'loading' ? LoadingStepIcon : undefined
                      }
                    >
                      {step.title}
                    </StepLabel>
                  )}
                </Step>
              );
            })}
          </Stepper>
          {children}
        </Paper>
      </Box>
    </>
  );
};

export default StepperPaper;
